import { useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import { products } from "@/mocks/products";

const packagingOptions = [
  { id: "classic", icon: "ri-gift-line", color: "from-amber-600 to-yellow-700", image: "https://readdy.ai/api/search-image?query=classic%20elegant%20gift%20box%20with%20cream%20ribbon%20and%20bow%20on%20white%20background%2C%20premium%20boutique%20packaging%2C%20soft%20and%20beautiful&width=300&height=200&seq=pkg1&orientation=landscape" },
  { id: "luxury", icon: "ri-vip-crown-line", color: "from-amber-400 to-yellow-500", image: "https://readdy.ai/api/search-image?query=luxury%20gold%20gift%20box%20with%20gold%20ribbon%20and%20bow%20on%20white%20background%2C%20premium%20boutique%20packaging%2C%20opulent%20and%20beautiful&width=300&height=200&seq=pkg2&orientation=landscape" },
  { id: "romantic", icon: "ri-heart-line", color: "from-rose-400 to-red-500", image: "https://readdy.ai/api/search-image?query=romantic%20pink%20rose%20gift%20box%20with%20pink%20ribbon%20and%20bow%20on%20white%20background%2C%20premium%20boutique%20packaging%2C%20romantic%20and%20beautiful&width=300&height=200&seq=pkg3&orientation=landscape" },
  { id: "corporate", icon: "ri-briefcase-line", color: "from-slate-400 to-gray-500", image: "https://readdy.ai/api/search-image?query=professional%20corporate%20gift%20box%20with%20navy%20ribbon%20and%20bow%20on%20white%20background%2C%20premium%20business%20packaging%2C%20elegant%20and%20sophisticated&width=300&height=200&seq=pkg4&orientation=landscape" },
];

export default function CustomGiftBoxPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [selectedPackaging, setSelectedPackaging] = useState("classic");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const toggleProduct = (id: number) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleWhatsApp = () => {
    const selected = selectedProducts.map((id) => {
      const p = products.find((pr) => pr.id === id);
      return p ? `• ${isFr ? p.nameF : p.name}` : "";
    });
    const pkg = packagingOptions.find((p) => p.id === selectedPackaging);
    const pkgLabel = pkg ? t(`cgb_packaging_${pkg.id}`) : "";
    const msg = encodeURIComponent(
      `Bonjour Creative Hub! Je voudrais commander une Boîte Cadeau Personnalisée:\n\nProduits:\n${selected.join("\n")}\n\nEmballage: ${pkgLabel}\n\nMessage: ${message || "Aucun"}\n\nMes coordonnées:\nNom: ${name}\nEmail: ${email}\nTéléphone: ${phone}`
    );
    window.open(`https://wa.me/237678012159?text=${msg}`, "_blank");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedProducts.length === 0) return;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#f5ede0]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-amber-900 via-rose-900 to-amber-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=custom%20gift%20box%20creation%20process%20with%20flowers%20candles%20and%20gourmet%20items%20on%20elegant%20cream%20table%2C%20luxury%20boutique%2C%20warm%20golden%20light%2C%20beautiful%20and%20romantic&width=1920&height=400&seq=cgb_hero&orientation=landscape"
            alt="Custom Gift Box"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center py-10">
          <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-3">— Personnalisé Pour Vous —</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("cgb_title")}
          </h1>
          <p className="text-white/70 text-lg">{t("cgb_subtitle")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        {submitted ? (
          <div className="max-w-lg mx-auto text-center py-20">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-6">
              <i className="ri-checkbox-circle-line text-4xl text-green-500"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Demande envoyée!" : "Request Sent!"}
            </h2>
            <p className="text-gray-500 mb-6">
              {isFr ? "Nous vous contacterons bientôt pour confirmer votre commande." : "We'll contact you soon to confirm your order."}
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="px-6 py-3 bg-gradient-to-r from-amber-600 to-rose-600 text-white font-semibold rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
            >
              {isFr ? "Créer une autre boîte" : "Create Another Box"}
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              {/* Step 1: Products */}
              <div className="bg-white rounded-2xl p-6 border border-amber-100">
                <h2 className="text-xl font-bold text-amber-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("cgb_products_title")}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {products.map((product) => {
                    const isSelected = selectedProducts.includes(product.id);
                    return (
                      <button
                        key={product.id}
                        onClick={() => toggleProduct(product.id)}
                        className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer text-left ${
                          isSelected ? "border-amber-500 scale-[1.02]" : "border-transparent hover:border-amber-200"
                        }`}
                      >
                        <div className="h-28 overflow-hidden">
                          <img src={product.image} alt={isFr ? product.nameF : product.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="p-2 bg-white">
                          <p className="text-xs font-semibold text-gray-700 truncate">{isFr ? product.nameF : product.name}</p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-amber-500">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2: Packaging */}
              <div className="bg-white rounded-2xl p-6 border border-amber-100">
                <h2 className="text-xl font-bold text-amber-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("cgb_packaging_title")}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {packagingOptions.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackaging(pkg.id)}
                      className={`rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                        selectedPackaging === pkg.id ? "border-amber-500 scale-[1.02]" : "border-transparent hover:border-amber-200"
                      }`}
                    >
                      <div className="h-24 overflow-hidden">
                        <img src={pkg.image} alt={t(`cgb_packaging_${pkg.id}`)} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className={`p-2 bg-gradient-to-r ${pkg.color} flex items-center gap-1.5`}>
                        <i className={`${pkg.icon} text-white text-xs`}></i>
                        <p className="text-xs font-semibold text-white truncate">{t(`cgb_packaging_${pkg.id}`)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Message & Details */}
              <div className="bg-white rounded-2xl p-6 border border-amber-100">
                <h2 className="text-xl font-bold text-amber-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("cgb_message_title")}
                </h2>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                    placeholder={t("cgb_message_placeholder")}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-300 transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right">{message.length}/500</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("cgb_name_label")} *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("cgb_email_label")}</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("cgb_phone_label")} *</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={selectedProducts.length === 0}
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-amber-600 to-rose-600 text-white font-semibold rounded-full hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-send-plane-line text-base"></i>
                      {t("cgb_submit")}
                    </button>
                    <button
                      type="button"
                      onClick={handleWhatsApp}
                      disabled={selectedProducts.length === 0}
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-500 text-white font-semibold rounded-full hover:bg-green-400 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-whatsapp-line text-base"></i>
                      {t("cgb_whatsapp")}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl p-6 border border-amber-100">
                <h3 className="text-lg font-bold text-amber-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Résumé de votre boîte" : "Your Box Summary"}
                </h3>

                {selectedProducts.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-3">
                      <i className="ri-gift-line text-2xl text-amber-400"></i>
                    </div>
                    <p className="text-gray-400 text-sm">{isFr ? "Sélectionnez des produits" : "Select products to begin"}</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 mb-5">
                    {selectedProducts.map((id) => {
                      const p = products.find((pr) => pr.id === id);
                      if (!p) return null;
                      return (
                        <div key={id} className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={p.image} alt={isFr ? p.nameF : p.name} className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-700 truncate">{isFr ? p.nameF : p.name}</p>
                          </div>
                          <button
                            onClick={() => toggleProduct(id)}
                            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Packaging */}
                <div className="py-4 border-t border-amber-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{isFr ? "Emballage" : "Packaging"}</span>
                    <span className="text-sm font-semibold text-gray-800">{t(`cgb_packaging_${selectedPackaging}`)}</span>
                  </div>
                </div>

                {/* Items count */}
                <div className="py-4 border-t border-amber-100">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">{isFr ? "Articles sélectionnés" : "Items Selected"}</span>
                    <span className="text-xl font-bold text-amber-700" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {selectedProducts.length}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{isFr ? "Nous vous enverrons un devis personnalisé" : "We'll send you a personalized quote"}</p>
                </div>

                {/* Message preview */}
                {message && (
                  <div className="py-4 border-t border-amber-100">
                    <p className="text-xs font-semibold text-gray-600 mb-2">{isFr ? "Votre message:" : "Your message:"}</p>
                    <p className="text-xs text-gray-500 italic leading-relaxed line-clamp-3">&ldquo;{message}&rdquo;</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}